import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { AuthService, Locale } from '@capturum/auth';
import { LoginAction, LoginConfig, LoginService as CapturumLoginService } from '@capturum/login';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, finalize, first } from 'rxjs/operators';
import { PublicLocaleApiService } from '@features/auth/services/public-locale-api.service';

@Injectable({ providedIn: 'root' })
export class LoginService extends CapturumLoginService {
  private loginError$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  private loginBusy$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    protected readonly config: LoginConfig,
    protected readonly router: Router,
    protected readonly translateService: TranslateService,
    protected readonly formBuilder: FormBuilder,
    private publicLocaleApiService: PublicLocaleApiService,
    private authService: AuthService,
  ) {
    super();

    this.getLocales()
      .pipe(first())
      .subscribe((locales) => {
        const locale = this.getDefaultLocaleCode(locales);

        this.setLanguage(locale);
      });
  }

  public setLanguage(language: string): void {
    this.translateService.reloadLang(language);
    this.translateService.use(language);
  }

  public getLoginError$(): Observable<string> {
    return this.loginError$.asObservable();
  }

  public isLoginBusy$(): Observable<boolean> {
    return this.loginBusy$.asObservable();
  }

  public login(data: { email: string; password: string; remember: boolean; locale_id: string }): Observable<any> {
    this.loginBusy$.next(true);

    const { email, password, remember, locale_id }: any = data;

    return this.authService.login(email, password, { remember, locale_id }).pipe(
      catchError((error) => {
        super.setAction(LoginAction.login, false, error.status);
        super.onLoginFail(error);
        this.loginError$.next('auth.sign-in.error');

        return throwError(error);
      }),
      finalize(() => {
        return this.loginBusy$.next(false);
      }),
    );
  }

  public onLoginSuccess(response: any): void {
    super.setAction(LoginAction.login, true);
    super.onLoginSuccess(response);
  }

  public getLocales(): Observable<Locale[]> {
    return this.publicLocaleApiService.getLocales();
  }

  public getDefaultLocaleCode(locales: Locale[]): string {
    const currentLanguage = this.translateService.currentLang;
    let hostLanguage = document.location.host.split('.').pop();

    hostLanguage = hostLanguage === 'ie' ? 'en' : hostLanguage;
    const isHostLanguageAvailable =
      hostLanguage &&
      locales?.some((locale) => {
        return locale.code === hostLanguage;
      });

    return isHostLanguageAvailable ? hostLanguage : currentLanguage;
  }

  public getCurrentTenant(): Observable<{ tenant_key: string }> {
    return this.publicLocaleApiService.apiHttp.get('/tenant_current');
  }
}
